import { useMemo } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import EndpointsEnum from "@models/EndpointsEnum";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import TokenTypesEnum from "@models/TokenTypesEnum";
import IGetRoutesDTO from "@models/DTO/IGetRoutesDTO";
import IOnGetRoutesDTO from "@models/DTO/IOnGetRoutesDTO";
import IGetAllSupplierTokensDTO from "@models/DTO/IGetAllSupplierTokensDTO";
import IOnGetAllSupplierTokensDTO from "@models/DTO/IOnGetAllSupplierTokensDTO";
import IOnTypeaheadLookupByTokenTypeDTO from "@models/DTO/IOnTypeaheadLookupByTokenTypeDTO";
import IOnGetVehiclesDTO from "@models/DTO/IOnGetVehiclesDTO";
import { IFilterSelectOption } from "@models/IFilterConfig";
import { buildAuthHeadersForFetch, buildRestUrlWithQuery } from "@services/RestService";
import { IDailyRoutesFilterLookups } from "@config/filters/dailyRoutesFilters";
import { requestOnce, useBaseDto } from "./useSocketLookups";

/** Page size for the by-guid resolvers, which scan a list because no single-record endpoint takes a guid. */
const RESOLVE_LIST_LIMIT = 500;

/**
 * Route / supplier user / vehicle typeaheads + by-guid resolvers for the Daily Routes FilterBar.
 * Lookups are the legacy screen's: routes over the socket, users and vehicles over REST.
 */
export function useDailyRouteLookups(): IDailyRoutesFilterLookups {
    const { socket, userModel, supplierModel } = useAppContextNonNull();
    const baseDto = useBaseDto();

    return useMemo(() => {
        const fetchJson = async <T>(url: string): Promise<T> => {
            const response = await fetch(url, { method: "GET", headers: buildAuthHeadersForFetch({ userModel, supplierModel }) });

            if (!response.ok) {
                throw new Error(`Lookup failed: ${response.status}`);
            }

            return (await response.json()) as T;
        };

        const getRoutes = (dto: Partial<IGetRoutesDTO>) =>
            requestOnce<IOnGetRoutesDTO>(socket, EndpointsEnum.RoutesGetRoutes, EndpointsEnum.RoutesOnGetRoutes, {
                ...baseDto(),
                ...dto,
            } as IGetRoutesDTO).then((data) => data.routes || []);

        const routes = (text: string): Promise<IFilterSelectOption[]> =>
            getRoutes({ name: text, limit: 10 }).then((found) => found.map((route) => ({ label: route.name, value: route.guid })));

        const resolveRoute = (guid: string): Promise<IFilterSelectOption | undefined> =>
            getRoutes({ limit: RESOLVE_LIST_LIMIT }).then((found) => {
                const route = found.find((r) => r.guid === guid);

                return route ? { label: route.name, value: guid } : undefined;
            });

        const users = (text: string): Promise<IFilterSelectOption[]> =>
            fetchJson<IOnTypeaheadLookupByTokenTypeDTO>(
                buildRestUrlWithQuery({
                    endpoint: RestEndpointsEnum.TokensSearch,
                    query: { tokenType: TokenTypesEnum.SUPPLIER_USER, name: text, limit: 10 },
                })
            ).then((data) =>
                (data.results || [])
                    .map((token) => token.User)
                    .filter((user) => !!user)
                    .map((user) => ({ label: user!.name, value: user!.guid }))
            );

        const resolveUser = (guid: string): Promise<IFilterSelectOption | undefined> => {
            const payload: IGetAllSupplierTokensDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                usersLimit: RESOLVE_LIST_LIMIT,
            };

            return requestOnce<IOnGetAllSupplierTokensDTO>(
                socket,
                EndpointsEnum.TokenGetAllSupplierTokens,
                EndpointsEnum.TokenOnGetAllSupplierTokens,
                payload
            ).then((data) => {
                const user = (data.tokens?.supplierUserTokens || []).map((token) => token.User).find((u) => u?.guid === guid);

                return user ? { label: user.name, value: guid } : undefined;
            });
        };

        const getVehicles = (query: { name?: string; limit: number }) =>
            fetchJson<IOnGetVehiclesDTO>(buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.GetVehicles, query })).then(
                (data) => data.vehicles || []
            );

        const vehicles = (text: string): Promise<IFilterSelectOption[]> =>
            getVehicles({ name: text, limit: 10 }).then((found) => found.map((vehicle) => ({ label: vehicle.name, value: vehicle.guid })));

        const resolveVehicle = (guid: string): Promise<IFilterSelectOption | undefined> =>
            getVehicles({ limit: RESOLVE_LIST_LIMIT }).then((found) => {
                const vehicle = found.find((v) => v.guid === guid);

                return vehicle ? { label: vehicle.name, value: guid } : undefined;
            });

        return { routes, resolveRoute, users, resolveUser, vehicles, resolveVehicle };
    }, [socket, baseDto, userModel, supplierModel]);
}
